import React, { Fragment, useState, useEffect } from "react";
import {
  Card,
  Col,
  Container,
  Row,
  Accordion,
  Button,
  Form,
  Table,
  Modal,
  Badge,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button as MuiButton,
} from "@mui/material";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import { ArrowBack, Close } from "@mui/icons-material";
import { MecordColors } from "Constants/Color";
import FollowupModel from "./FollowupModel";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import Swal from "sweetalert2";
import { showCustomSwal, showConfirm, showSuccess } from "helpers/swalHelper";
import { fetchPaymentHistory, addRecordPayment, removeRecordPayment, addFollowUp } from "slices/paymentmanagement/thunk";
import { fetchProductSalesOrderById, fetchServiceSalesOrderById } from "slices/sales_order/thunk";
import { toast } from "react-toastify";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import LoadingSpinner from "components/LoadingSpinner";
import CancelBackButton from "Common/Components/Buttons/CancelBackButton";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { formatIndianAmount } from "helpers/formatIndianAmount";

const RecordPayment = () => {
  document.title = "Record Payment";
  const navigate = useNavigate();
  const params = useParams<{ order_type: string; payment_type: string; id: string }>();
  const [showFollowUpModal, setShowFollowUpModal] = useState<boolean>(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);
  const [isUpdatingPacking, setIsUpdatingPacking] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAddingPayment, setIsAddingPayment] = useState<boolean>(false);

  const dispatch = useDispatch<any>();
  const selectPaymentManagementState = (state: any) => state.PaymentManagement;
  const selectPaymentHistory = createSelector(
    [selectPaymentManagementState],
    (state) => state.paymentHistory || []
  );
  const paymentRecords = useSelector(selectPaymentHistory);

  // Extract order_type and payment_type from URL params
  const orderType = params.order_type
    ? params.order_type.charAt(0).toUpperCase() + params.order_type.slice(1).toLowerCase()
    : "";

  const paymentType = params.payment_type
    ? params.payment_type === "order"
      ? "Order Payment"
      : params.payment_type.charAt(0).toUpperCase() + params.payment_type.slice(1).toLowerCase()
    : "";

  const orderId = params.id || "";

  const fetchOrderData = async () => {
    if (!orderId || !orderType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let result: any;

      if (orderType === "Product") {
        result = await dispatch(fetchProductSalesOrderById(orderId));
      } else if (orderType === "Service") {
        result = await dispatch(fetchServiceSalesOrderById(orderId));
      }

      if (result && !result.error) {
        const data = result.payload?.data || result.payload;
        setOrderData(data);

        // Fetch payment history after order data is loaded
        dispatch(fetchPaymentHistory({
          orderId: orderId,
          orderType: orderType,
          paymentType: paymentType
        }));
      } else {
        toast.error("Failed to fetch order data");
      }
    } catch (error: any) {
      console.error("Error fetching order data:", error);
      toast.error(error?.message || "Failed to fetch order data");
    } finally {
      setLoading(false);
    }
  };


  // Fetch order data by ID
  useEffect(() => {
    fetchOrderData();
  }, [dispatch, orderId, orderType]);
  
  const RETENTION_AMOUNT = orderData?.retention_amount || 0;
  const TOTAL_ORDER_AMOUNT = paymentType === "Retention" ? RETENTION_AMOUNT : orderData?.grand_total_amount || orderData?.total_amount || 0;

  // Get currency symbol: For Product orders use orderData currency_symbol, for Service always use ₹
  const currencySymbol = orderType === "Product"
    ? (orderData?.currency_symbol || "₹")
    : "₹";

  const handleDeleteRecord = (id: number) => {
    showConfirm(
      "You won't be able to revert this!",
      "Are you sure?",
      "Yes, delete it!",
      "No, cancel"
    ).then((result) => {
      if (result.isConfirmed) {
        dispatch(removeRecordPayment(id.toString())).then(() => {
          showSuccess("Your record has been deleted.", "Deleted!");
        });
      }
    });
  };

  const paymentModes = ["Cash", "UPI", "Cheque", "Bank Transfer"];

  // Calculate current total paid to determine remaining balance
  const totalPaid = paymentRecords.reduce(
    (sum: number, record: any) => sum + record.receivedAmount,
    0
  );
  const remainingBalance = TOTAL_ORDER_AMOUNT - totalPaid;
  const isPaymentComplete = remainingBalance <= 0;

  const isRetentionPayment = paymentType === "Retention";
  const maxReceivableAmount = Math.max(0, remainingBalance);

  const validationSchema = Yup.object().shape({
    modeOfPayment: Yup.string().required("Mode of Payment is required"),
    amountReceived: Yup.number()
      .typeError("Must be a number")
      .required("Amount Received is required")
      .positive("Must be positive")
      .test(
        "not-zero-pending",
        "No pending amount remaining for this order.",
        () => remainingBalance > 0
      )
      .test(
        "not-greater-than-pending",
        `Amount received cannot be greater than amount pending (${currencySymbol} ${formatIndianAmount(maxReceivableAmount)})`,
        (value) => {
          if (value == null) return true;
          return value <= maxReceivableAmount;
        }
      ),
    transactionRef: Yup.string().optional(),
    remark: Yup.string().optional(),
    paymentDate: Yup.string().required("Payment Date is required"),
  });

  const formik = useFormik({
    initialValues: {
      modeOfPayment: "",
      amountReceived: "",
      transactionRef: "",
      remark: "",
      paymentDate: new Date().toISOString().split("T")[0],
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (remainingBalance <= 0) {
        toast.warning("Order is already fully paid. No more payments can be recorded.");
        return;
      }
      const isProduct = orderType === "Product";

      // Convert local date to UTC ISO string
      const convertLocalDateToUTC = (localDateStr: string): string => {
        if (!localDateStr) return "";
        try {
          const [year, month, day] = localDateStr.split("-").map(Number);
          if (isNaN(year) || isNaN(month) || isNaN(day)) return "";
          const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
          if (isNaN(localDate.getTime())) return "";
          return localDate.toISOString();
        } catch (error) {
          console.error("Error converting local date to UTC:", error);
          return "";
        }
      };

      const payload: any = {
        order_type: orderType,
        payment_type: paymentType,
        received_amount: Number(values.amountReceived),
        amount_pending: Math.max(0, projectedPending), // Clamp to 0, never negative
        payment_date: convertLocalDateToUTC(values.paymentDate),
        mode_of_payment: values.modeOfPayment,
        transaction_ref_no: values.transactionRef,
        remark: values.remark,
        created_by: "system", // Should ideally come from auth, but using "system" or similar as per backend data.created_by
      };

      if (isProduct) {
        payload.sales_order_product_id = orderId;
      } else {
        payload.sales_order_service_id = orderId;
      }

      console.log("Adding payment payload:", payload);
      setIsAddingPayment(true);
      dispatch(addRecordPayment(payload)).then((result: any) => {
        setIsAddingPayment(false);
        if (!result.error) {
          showSuccess("Payment record added successfully", "Success");
          formik.resetForm();
          // Refresh history
          dispatch(fetchPaymentHistory({
            orderId: orderId,
            orderType: orderType,
            paymentType: paymentType
          }));
        } else {
          toast.error(result.payload?.message || "Failed to add payment record");
        }
      }).catch((error: any) => {
        setIsAddingPayment(false);
        toast.error(error?.message || "Failed to add payment record");
      });
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  // Calculate the projected pending amount based on user input
  // Clamp to 0 to prevent negative values (overpayments show as 0 pending)
  const inputAmount = parseFloat(formik.values.amountReceived) || 0;
  const projectedPending = Math.max(0, remainingBalance - inputAmount);

  // Display pending amount (clamped to 0, never negative)
  const displayPending = projectedPending;

  const commonInputStyle = {
    fontFamily: "Poppins, sans-serif",
    fontSize: "13px",
    borderRadius: "8px",
  };

  const handleInitiatePacking = async () => {
    if (!orderId) {
      toast.error("Sales order ID is missing");
      return;
    }

    try {
      setIsUpdatingPacking(true);
      const payload = {
        packing_status: "Packing Initiated",
      };

      // Make PATCH request to update packing status
      await Axios.patch(
        API_ENDPOINT.SALES_ORDER.PRODUCT.STATUS_UPDATE(orderId),
        payload
      );

      toast.success("Packing status updated to 'Packing Initiated' successfully");
    } catch (error: any) {
      console.error("Failed to update packing status:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update packing status"
      );
    } finally {
      setIsUpdatingPacking(false);
      fetchOrderData();
    }
  };

  const PackingDispatchStatus = ["Packing Initiated", "Issued for Packing", "Issued for packing", "Partially Packed", "partially packed", "Packed", "Partially Dispatched", "Dispatched"];


  return (
    <PageContainer >
      <style>{`
        .custom-accordion .accordion-button:not(.collapsed) {
            background-color: transparent !important;
            box-shadow: none !important;
            color: inherit !important;
        }
        .custom-accordion .accordion-button {
            background-color: transparent !important;
            box-shadow: none !important;
            padding: 0;
        }
        .custom-accordion .accordion-item {
            background-color: transparent !important;
        }
        .custom-accordion .accordion-button::after {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z' stroke='%23212529' stroke-width='1'/%3e%3c/svg%3e") !important;
            background-size: 1rem;
            width: 1rem;
            height: 1rem;
            margin-right: 10px;
        }
      `}</style>
      <>
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3 "
          style={{ borderBottomColor: "rgba(223, 223, 223, 0.9)" }}
        >
          <div
            className="d-flex align-items-center"
            style={{ gap: "10px", cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            <ArrowBack
              className="me-2"
              style={{ fontSize: "1.25rem", cursor: "pointer" }}
            />
            <p className="mb-0 fw-bold" style={{ fontSize: "20px" }}>
              Record Payment
            </p>
          </div>
          <div className="d-flex gap-2">

            {!loading && 
             !PackingDispatchStatus.map(status => status.toLowerCase()).includes((orderData?.packing_status || "").toLowerCase()) &&
              orderType === "Product" &&
              paymentType === "Order Payment" && (
                <Button
                  variant="danger"
                  // disabled={paymentRecords.length === 0}
                  onClick={() => {
                    showConfirm(
                      "Are you sure you want to initiate packing?",
                      "Confirm Initiation",
                      "Yes, initiate!",
                      "Cancel"
                    ).then((result) => {
                      if (result.isConfirmed) {
                        handleInitiatePacking()
                      }
                    });
                  }}
                  className="px-3 py-1"
                  style={{
                    backgroundColor: MecordColors.PrimaryButtonBGColor,
                    borderColor: MecordColors.PrimaryButtonBGColor,
                    borderRadius: "8px",
                    fontSize: "13px",
                    height: "40px",
                  }}
                >
                  Initiate Packing
                </Button>
              )}
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Row>
              <Col lg={12}>
                {/* Sales Order Details Accordion */}
                <div
                  className="mb-4 border-bottom pb-3 bg-gray-100"
                  style={{ borderBottomColor: "rgba(223, 223, 223, 0.1)" }}
                >
                  <Accordion className="custom-accordion">
                    <Accordion.Item
                      eventKey="0"
                      className="border-0 shadow-none"
                      style={{ backgroundColor: "transparent" }}
                    >
                      <Accordion.Header className="bg-transparent p-0">
                        <p
                          className="mb-0 fw-bold"
                          style={{ fontSize: "20px" }}
                        >
                          Sales Order Details
                        </p>
                      </Accordion.Header>
                      <Accordion.Body
                        className="px-0"
                        style={{ backgroundColor: "transparent" }}
                      >
                        <div
                          className="rounded-3 p-4"
                          style={{ backgroundColor: "#FFF5F5" }}
                        >
                          {/* SO Basic Details */}
                          <Row className="mb-3">
                            <Col md={6}>
                              <Row className="mb-2">
                                <Col xs={4}>
                                  <span
                                    className="fw-bold"
                                    style={{
                                      color: "#333",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Sales Order No. :
                                  </span>
                                </Col>
                                <Col xs={8}>
                                  <span
                                    style={{ color: "#333", fontSize: "13px" }}
                                  >
                                    {orderData?.sales_order_number || orderData?.service_number || "-"}
                                  </span>
                                </Col>
                              </Row>
                              <Row>
                                <Col xs={4}>
                                  <span
                                    className="fw-bold"
                                    style={{
                                      color: "#333",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Initiated By :
                                  </span>
                                </Col>
                                <Col xs={8}>
                                  <span
                                    style={{ color: "#333", fontSize: "13px" }}
                                  >
                                    {orderData?.initiated_by || "-"}
                                  </span>
                                </Col>
                              </Row>
                            </Col>
                            <Col md={6}>
                              <Row className="mb-2">
                                <Col xs={4}>
                                  <span
                                    className="fw-bold"
                                    style={{
                                      color: "#333",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Sales Order Date :
                                  </span>
                                </Col>
                                <Col xs={8}>
                                  <span
                                    style={{ color: "#333", fontSize: "13px" }}
                                  >
                                    {orderData?.sales_order_date
                                      ? moment(orderData.sales_order_date).format("DD-MMM-YY")
                                      : orderData?.srv_date
                                        ? moment(orderData.srv_date).format("DD-MMM-YY")
                                        : orderData?.service_date
                                          ? moment(orderData.service_date).format("DD-MMM-YY")
                                          : "-"}
                                  </span>
                                </Col>
                              </Row>
                              <Row>
                                <Col xs={4}>
                                  <span
                                    className="fw-bold"
                                    style={{
                                      color: "#333",
                                      fontSize: "13px",
                                    }}
                                  >
                                    Total Order Amount
                                  </span>
                                </Col>
                                <Col xs={8}>
                                  <span
                                    style={{ color: "#333", fontSize: "13px" }}
                                  >
                                    {currencySymbol} {formatIndianAmount(TOTAL_ORDER_AMOUNT)}
                                  </span>
                                </Col>
                              </Row>
                            </Col>
                          </Row>

                          {/* Customer Details Section */}
                          <div className="mt-4">
                            <h6
                              className="fw-bold mb-3"
                              style={{ fontSize: "14px", color: "#333" }}
                            >
                              Customer Details
                            </h6>
                            <Row>
                              <Col md={6}>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Company Name :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.company_name || orderData?.customer_name || "-"}
                                    </span>
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      GST Type :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.gst_type || "-"}
                                    </span>
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Company Email :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.email || "-"}
                                    </span>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Company Phone No. :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.phone_number || "-"}
                                    </span>
                                  </Col>
                                </Row>
                              </Col>
                              <Col md={6}>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Industry :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.industry || "-"}
                                    </span>
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      style={{
                                        color: "transparent",
                                        fontSize: "13px",
                                      }}
                                    >
                                      -
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "transparent",
                                        fontSize: "13px",
                                      }}
                                    >
                                      -
                                    </span>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </div>

                          {/* Contact Details Section */}
                          <div className="mt-4">
                            <h6
                              className="fw-bold mb-3"
                              style={{ fontSize: "14px", color: "#333" }}
                            >
                              Contact Details
                            </h6>
                            <Row>
                              <Col md={6}>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Contact Name :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.contact_details?.[0]?.name || "-"}
                                    </span>
                                  </Col>
                                </Row>
                                <Row className="mb-2">
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Email :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.contact_details?.[0]?.email || "-"}
                                    </span>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col xs={5}>
                                    <span
                                      className="fw-bold"
                                      style={{
                                        color: "#333",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Phone Number :
                                    </span>
                                  </Col>
                                  <Col xs={7}>
                                    <span
                                      style={{
                                        color: "#666",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {orderData?.customer_details?.contact_details?.[0]?.phone_number || "-"}
                                    </span>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </div>

                          {/* Retention Details Section */}
                          {paymentType === "Retention" && orderData?.retention_applicable && (
                            <div className="mt-4">
                              <h6
                                className="fw-bold mb-3"
                                style={{ fontSize: "14px", color: "#333" }}
                              >
                                Retention Details
                              </h6>
                              <Row>
                                <Col md={6}>
                                  <Row className="mb-2">
                                    <Col xs={5}>
                                      <span
                                        className="fw-bold"
                                        style={{
                                          color: "#333",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Retention Type :
                                      </span>
                                    </Col>
                                    <Col xs={7}>
                                      <span
                                        style={{
                                          color: "#333",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {orderData?.retention_type || "-"}
                                      </span>
                                    </Col>
                                  </Row>
                                  <Row className="mb-2">
                                    <Col xs={5}>
                                      <span
                                        className="fw-bold"
                                        style={{
                                          color: "#333",
                                          fontSize: "13px",
                                        }}
                                      >
                                        Retention Amount :
                                      </span>
                                    </Col>
                                    <Col xs={7}>
                                      <span
                                        style={{
                                          color: "#333",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {currencySymbol} {formatIndianAmount(orderData?.retention_amount)}
                                      </span>
                                    </Col>
                                  </Row>
                                  {orderData?.retention_type?.toLowerCase() === "percentage" && (
                                    <Row className="mb-2">
                                      <Col xs={5}>
                                        <span
                                          className="fw-bold"
                                          style={{
                                            color: "#333",
                                            fontSize: "13px",
                                          }}
                                        >
                                          Percentage:
                                        </span>
                                      </Col>
                                      <Col xs={7}>
                                        <span
                                          style={{
                                            color: "#333",
                                            fontSize: "13px",
                                          }}
                                        >
                                          {orderData?.retention_percentage ? `${orderData?.retention_percentage}%` : "-"}
                                        </span>
                                      </Col>
                                    </Row>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          )}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>

                {/* Terms & Conditions */}
                <div className="mb-4">
                  <h5 className="mb-3 fw-bold" style={{ fontSize: "20px" }}>Terms & Conditions</h5>
                  <div
                    className="border rounded-3 overflow-hidden bg-white p-3"
                    style={{ borderColor: "#e9ebec" }}
                  >
                    {(() => {
                      if (!orderData?.terms_and_conditions || orderData.terms_and_conditions.length === 0) {
                        return (
                          <div className="p-3 text-center text-muted fs-13">
                            No Terms & Conditions available
                          </div>
                        );
                      }

                      // Group terms by type to handle multiple options per type
                      const groupedTerms: Record<string, string[]> = {};
                      orderData.terms_and_conditions.forEach((item: any) => {
                        if (item.type) {
                          if (!groupedTerms[item.type]) {
                            groupedTerms[item.type] = [];
                          }
                          // Handle both string and array options
                          if (Array.isArray(item.options)) {
                            item.options.forEach((opt: string) => {
                              if (
                                opt &&
                                !groupedTerms[item.type].includes(opt)
                              ) {
                                groupedTerms[item.type].push(opt);
                              }
                            });
                          } else if (item.options) {
                            // If options is a string, check if it's newline-separated
                            const options = item.options
                              .split("\n")
                              .filter((opt: string) => opt.trim());
                            options.forEach((opt: string) => {
                              if (
                                opt &&
                                !groupedTerms[item.type].includes(opt)
                              ) {
                                groupedTerms[item.type].push(opt);
                              }
                            });
                          }
                        }
                      });

                      return Object.entries(groupedTerms).map(
                        ([type, options], idx) => (
                          <div key={idx} className="mb-3">
                            <strong
                              style={{
                                fontSize: "0.875rem",
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "600",
                              }}
                            >
                              {type}
                            </strong>
                            <ul
                              style={{
                                fontSize: "0.813rem",
                                color: "#666",
                                marginLeft: "20px",
                                paddingLeft: "0",
                                listStyle: "disc",
                              }}
                            >
                              {options.map(
                                (option: string, optIdx: number) => (
                                  <li
                                    key={optIdx}
                                    style={{ marginBottom: "4px" }}
                                  >
                                    {option}
                                  </li>
                                )
                              )}
                            </ul>
                            {idx <
                              Object.keys(groupedTerms).length - 1 && (
                                <hr
                                  style={{
                                    width: "100%",
                                    marginTop: "12px",
                                    borderTop: "1px solid #e9ebec",
                                    opacity: 1,
                                  }}
                                />
                              )}
                          </div>
                        )
                      );
                    })()}
                  </div>
                </div>

                {/* Record Payment Form - Hidden when fully paid */}
                {!isPaymentComplete && (
                  <div className="mb-4">
                    <h5 className="mb-3 fw-bold" style={{ fontSize: "20px" }}>
                      Record Payment
                    </h5>
                    <div
                      className="rounded-3 p-4"
                      style={{ backgroundColor: "#FFF5F5" }}
                    >
                      <Row className="gy-3">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label className="fw-bold fs-13 text-dark">
                              Payment Date<span className="text-danger">*</span>
                            </Form.Label>
                          <Form.Control
                            type="date"
                            name="paymentDate"
                            value={formik.values.paymentDate}
                            onChange={(e) => {
                              formik.handleChange(e);
                              if ((e.target.value || "").trim()) setTimeout(() => formik.setFieldError("paymentDate", undefined), 0);
                            }}
                            onBlur={formik.handleBlur}
                            isInvalid={
                              !!formik.errors.paymentDate &&
                              formik.touched.paymentDate
                            }
                            style={{
                              ...commonInputStyle,
                              borderColor: "#ced4da",
                              height: "40px",
                            }}
                            className="bg-white shadow-none"
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.paymentDate}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold fs-13 text-dark">
                            Mode of Payment<span className="text-danger">*</span>
                          </Form.Label>
                          <Autocomplete
                            options={paymentModes}
                            value={formik.values.modeOfPayment || null}
                            onChange={(event, newValue) => {
                              formik.setFieldValue(
                                "modeOfPayment",
                                newValue || ""
                              );
                              if (newValue) setTimeout(() => formik.setFieldError("modeOfPayment", undefined), 0);
                            }}
                            onBlur={() =>
                              formik.setFieldTouched("modeOfPayment", true)
                            }
                            ListboxProps={autocompleteDropdownStyles.ListboxProps}
                            renderOption={(props, option) =>
                              autocompleteDropdownStyles.renderOption(
                                props,
                                option,
                                (opt) => opt
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                inputProps={{ ...params.inputProps, id: "modeOfPayment", name: "modeOfPayment" }}
                                placeholder="Select"
                                variant="outlined"
                                size="small"
                                error={
                                  !!formik.errors.modeOfPayment &&
                                  formik.touched.modeOfPayment
                                }
                                helperText={
                                  formik.touched.modeOfPayment
                                    ? formik.errors.modeOfPayment
                                    : ""
                                }
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                  "& .MuiOutlinedInput-root": {
                                    height: "40px",
                                    backgroundColor: "#fff",
                                    "& fieldset": {
                                      borderColor: "#ced4da",
                                      borderRadius: "8px",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold fs-13 text-dark">
                            Amount Received<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Enter Amount"
                            name="amountReceived"
                            min={0}
                            value={formik.values.amountReceived}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                formik.setFieldValue("amountReceived", "");
                                return;
                              }

                              const parsed = Number(raw);
                              if (!Number.isFinite(parsed)) {
                                formik.setFieldValue("amountReceived", raw);
                                return;
                              }

                              // Clamp so the user cannot input more than pending amount
                              const clamped = Math.min(parsed, maxReceivableAmount);
                              formik.setFieldValue("amountReceived", clamped.toString());
                              if (clamped > 0 && clamped <= maxReceivableAmount) {
                                setTimeout(() => formik.setFieldError("amountReceived", undefined), 0);
                              }
                            }}
                            onBlur={formik.handleBlur}
                            max={maxReceivableAmount}
                            disabled={isPaymentComplete}
                            isInvalid={
                              !!formik.errors.amountReceived &&
                              formik.touched.amountReceived
                            }
                            style={{
                              ...commonInputStyle,
                              borderColor: "#ced4da",
                              height: "40px",
                            }}
                            className="bg-white shadow-none"
                          />
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.amountReceived}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold fs-13 text-dark">
                            Amount Pending
                          </Form.Label>
                          <div
                            style={{
                              ...commonInputStyle,
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              padding: "0 12px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                              border: "1px solid #e9ecef",
                              color: "#333",
                              fontWeight: "500"
                            }}
                          >
                              {`${currencySymbol} ${formatIndianAmount(displayPending)}`}
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold fs-13 text-dark">
                            Transaction Ref (UPI No./ Cheque No.)
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter"
                            name="transactionRef"
                            value={formik.values.transactionRef}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            style={{
                              ...commonInputStyle,
                              borderColor: "#ced4da",
                              height: "40px",
                            }}
                            className="bg-white shadow-none"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold fs-13 text-dark">
                            Remarks
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter Remarks"
                            name="remark"
                            value={formik.values.remark}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            style={{
                              ...commonInputStyle,
                              borderColor: "#ced4da",
                              height: "40px",
                            }}
                            className="bg-white shadow-none"
                          />
                        </Form.Group>
                      </Col>

                      <Col md={3} className="d-flex justify-content-end" style={{ paddingTop: "28px" }}>
                        <Button
                          variant="danger"
                          onClick={() => formik.handleSubmit()}
                          disabled={isAddingPayment || isPaymentComplete}
                          className="px-4 d-flex align-items-center gap-2"
                          style={{
                            backgroundColor: isAddingPayment || isPaymentComplete ? "#ccc" : MecordColors.PrimaryButtonBGColor,
                            borderColor: isAddingPayment || isPaymentComplete ? "#ccc" : MecordColors.PrimaryButtonBGColor,
                            borderRadius: "8px",
                            height: "40px",
                            width: "100%",
                            justifyContent: "center",
                            cursor: isAddingPayment || isPaymentComplete ? "not-allowed" : "pointer"
                          }}
                          onMouseOver={(e) => {
                            if (!isAddingPayment && !isPaymentComplete) {
                              e.currentTarget.style.backgroundColor =
                                MecordColors.PrimaryButtonHoverBGColor;
                              e.currentTarget.style.borderColor =
                                MecordColors.PrimaryButtonHoverBGColor;
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isAddingPayment && !isPaymentComplete) {
                              e.currentTarget.style.backgroundColor =
                                MecordColors.PrimaryButtonBGColor;
                              e.currentTarget.style.borderColor =
                                MecordColors.PrimaryButtonBGColor;
                            }
                          }}
                        >
                          {isAddingPayment ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              <span>Adding...</span>
                            </>
                          ) : (
                            <>
                              <i className="bi bi-plus fs-4"></i>
                              <span>Add</span>
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </div>
                )}

                {/* Payment Table */}
                <div className="mb-0">
                  {isPaymentComplete && (
                    <div className="mb-3 d-flex justify-content-start">
                      <Badge
                        bg="success"
                        style={{
                          fontSize: "0.875rem",
                          padding: "8px 16px",
                          borderRadius: "20px",
                          fontWeight: "500",
                        }}
                      >
                        ✓ No Pending Amount
                      </Badge>
                    </div>
                  )}
                  <div className="table-responsive">
                    <Table
                      className="table-nowrap mb-0 align-middle"
                      style={{ borderCollapse: "collapse" }}
                    >
                      <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                        <tr>
                          <th
                            scope="col"
                            className="fw-bold ps-4 py-3"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Sr No.
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Payment Date
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            {orderData?.retention_applicable && paymentType === "Retention"
                              ? "Total Retention Amount"
                              : "Total Order Amount"}
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Received Amount
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Pending Amount
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Mode of Payment
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Transaction Ref
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-start"
                            style={{ color: MecordColors.NormalText }}
                          >
                            Remarks
                          </th>
                          <th
                            scope="col"
                            className="fw-bold py-3 text-center"
                            style={{
                              color: MecordColors.NormalText,
                              position: "sticky",
                              right: 0,
                              backgroundColor: MecordColors.LightGrayColor,
                              zIndex: 2,
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentRecords.length > 0 ? (
                          paymentRecords.map((record: any, index: number) => (
                            <tr key={record.id} className="bg-white border-bottom">
                              <td className="fw-medium ps-4 py-3">{index + 1}</td>
                              <td className="py-3 text-start">{record.date}</td>
                              <td className="py-3 text-start">
                                {currencySymbol}{" "}
                                {formatIndianAmount(
                                  orderData?.retention_applicable && paymentType === "Retention"
                                    ? RETENTION_AMOUNT
                                    : record.totalAmount
                                )}
                              </td>
                              <td className="py-3 text-start">
                                {currencySymbol} {formatIndianAmount(record.receivedAmount)}
                              </td>
                              <td className="py-3 text-start">
                                {currencySymbol} {formatIndianAmount(record.pendingAmount)}
                              </td>
                              <td className="py-3 text-start">{record.modeOfPayment}</td>
                              <td className="py-3 text-start">{record.transactionRef || "-"}</td>
                              <td className="py-3 text-start">{record.remark || "-"}</td>
                              <td
                                className="py-3 text-center"
                                style={{
                                  position: "sticky",
                                  right: 0,
                                  backgroundColor: "#fff",
                                  zIndex: 1,
                                }}
                              >
                                <IconButton
                                  onClick={() => handleDeleteRecord(record.id)}
                                  size="small"
                                  sx={{ color: MecordColors.PrimaryButtonBGColor }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="bg-white border-bottom">
                            <td colSpan={9} className="text-center py-4 text-muted">
                              No payments recorded yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>

              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 mt-4">
              <CancelButton
                onClick={() => navigate(-1)}
                showConfirmation={false}
              />
            </div>
          </>
        )}
      </>
    </PageContainer>
  );
};

export default RecordPayment;
